import { useEffect, useState } from "react";
import {
  BsCheckCircle,
  BsExclamationTriangle,
  BsInfoCircle,
  BsX,
  BsXCircle,
} from "react-icons/bs";
import { GrNotification } from "react-icons/gr";

import Image from "next/image";
import "./style.css";

export interface ToastProps {
  id: string | number;
  type: "info" | "success" | "error" | "alert" | "notification";
  label: string;
  message: string;
  duration?: number;
  urlImage?: string;
  urlNotification?: string;
  onRemoveMessage?(id: number | string): void;
}

export function ToastMessage({
  id,
  message,
  type,
  label,
  duration,
  urlImage,
  onRemoveMessage,
}: ToastProps) {
  const [visible, setVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const showTimeout = setTimeout(() => {
      setVisible(true);
    }, 50);

    const timeoutId = setTimeout(() => {
      handleRemoveToast();
    }, duration || 5000); // 5s

    return () => {
      clearTimeout(showTimeout);
      clearTimeout(timeoutId);
    };
  }, [id, duration]);

  function handleRemoveToast() {
    setIsExiting(true);
    setTimeout(() => {
      onRemoveMessage!(id);
    }, 300);
  }

  const getIconAndColor = () => {
    switch (type) {
      case "success":
        return {
          icon: <BsCheckCircle size={20} className="shrink-0" />,
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
          iconColor: "text-green-600",
          barColor: "bg-green-500",
          iconBgClass: "bg-green",
        };
      case "info":
        return {
          icon: <BsInfoCircle size={20} className="shrink-0" />,
          bgColor: "bg-blue-50",
          borderColor: "border-blue-200",
          iconColor: "text-blue-600",
          barColor: "bg-blue-500",
          iconBgClass: "bg-blue",
        };
      case "alert":
        return {
          icon: <BsExclamationTriangle size={20} className="shrink-0" />,
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-200",
          iconColor: "text-yellow-600",
          barColor: "bg-yellow-500",
          iconBgClass: "bg-yellow",
        };
      case "error":
        return {
          icon: <BsXCircle size={20} className="shrink-0" />,
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          iconColor: "text-red-600",
          barColor: "bg-red-500",
          iconBgClass: "bg-red",
        };
      case "notification":
        return {
          icon: urlImage ? (
            <Image
              src={urlImage}
              alt="Notification"
              width={20}
              height={20}
              className="shrink-0 rounded-full"
            />
          ) : (
            <GrNotification size={20} className="shrink-0" />
          ),
          bgColor: "bg-white",
          borderColor: "border-gray-200",
          iconColor: "text-azulfy-blue",
          barColor: "bg-azulfy-blue",
          iconBgClass: "bg-notification",
        };
      default:
        return {
          icon: <BsInfoCircle size={20} className="shrink-0" />,
          bgColor: "bg-gray-50",
          borderColor: "border-gray-200",
          iconColor: "text-gray-600",
          barColor: "bg-gray-500",
          iconBgClass: "bg-gray",
        };
    }
  };

  const { icon, bgColor, borderColor, iconColor, iconBgClass } =
    getIconAndColor();

  return (
    <article
      className={`toast-item ${bgColor} ${borderColor} ${
        visible && !isExiting ? "toast-enter" : ""
      } ${isExiting ? "toast-exit" : ""}`}
    >
      {/* Content */}
      <div className="flex items-start gap-3 pl-5 pr-3 py-4">
        <div className={`toast-icon ${iconBgClass} ${iconColor}`}>{icon}</div>

        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-azulfy-rich-black text-sm leading-tight mb-0.5">
            {label}
          </h4>
          <p className="text-xs text-gray-600 leading-relaxed wrap-break-word">
            {message}
          </p>
        </div>

        <button
          onClick={handleRemoveToast}
          className="toast-close-btn shrink-0 hover:cursor-pointer"
          aria-label="Close notification"
          title="Close notification"
        >
          <BsX size={20} />
        </button>
      </div>
    </article>
  );
}
